<template lang="pug">
  div(
    v-if="showActions"
    class="t-orm-table-actions"
    :class="config.classes"
  )
    slot(name="prepend")
    component(
      v-if="prependComponent"
      :is="prependComponent"
      v-bind="prependComponentProps"
      :item="item"
      :row-index="rowIndex"
    )
    v-menu(
      v-model="isOpened"
      v-bind="menuOptions"
    )
      template(#activator="{ on }")
        v-btn(
          icon
          v-on="on"
          :disabled="disabled"
          v-bind="activatorOptions"
          :data-test="`table-item-actions-button-${rowIndex}`"
        )
          v-icon {{ customIcon }}
      v-list(
        dense
        class="menu-list py-0"
      )
        template(v-for="(action, idx) in actions")
          template(v-if="action.innerMenu")
            v-menu(
              right
              offset-x
            )
              template(v-slot:activator="{ on }")
                template(v-if="isDisabled(action)")
                  v-tooltip(top)
                    template(v-slot:activator="{ on, attrs }")
                      v-list-item(
                        v-if="isVisible(action, item)"
                        :key="idx"
                        v-on="on"
                        v-bind="attrs"
                        class="menu-list__item disabled"
                        :data-test="`action-${action?.name}`"
                      )
                        v-list-item-title(class="text-14px dark") {{ $t(action.text) }}
                        v-list-item-icon(
                          v-if="action.icon"
                          class="ma-0 h-100 d-flex align-center"
                        )
                          e-svg-icon(:name="action.icon")
                    span {{ $t(action.tooltip) }}
                template(v-else)
                  v-list-item(
                    v-if="isVisible(action, item)"
                    :key="idx"
                    v-on="on"
                    class="menu-list__item"
                    :data-test="`action-${action?.name}`"
                  )
                    v-list-item-title(class="text-14px dark") {{ $t(action.text) }}
                    v-list-item-icon(
                      v-if="action.icon"
                      class="ma-0 h-100 d-flex align-center"
                    )
                      e-svg-icon(:name="action.icon")
              v-list(class="menu-list")
                v-subheader {{ $t(action.innerMenuTitle) }}
                v-list-item(
                  v-for="(option, idx) in action.innerMenu"
                  :key="idx"
                  class="menu-list__item"
                  :data-test="`action-${action?.name}`"
                  @click="handleOptionClick(option)"
                )
                  v-list-item-icon(v-if="option.icon")
                    e-svg-icon {{ option.icon }}
                  v-list-item-title(class="text-14px dark") {{ $t(option.text) }}
                  v-list-item-icon(
                    v-if="item[action.innerMenuItemValue] === option.value"
                    class="ma-0 h-100 d-flex align-center"
                  )
                    e-svg-icon check-primary
          template(v-else-if="action.component")
            component(
              :is="action.component"
              v-bind="setProps(action.componentProps)"
            )
              template(#customActivator="{ handleClick }")
                v-list-item(
                  v-if="isVisible(action, item)"
                  :key="idx"
                  @click="handleClick"
                  class="menu-list__item"
                  :data-test="`action-${action?.name}`"
                )
                  v-list-item-title(
                    class="text-14px"
                    :class="[isRemoveAction(action) ? 'color-error' : 'dark']"
                  ) {{ $t(action.text) }}
                  v-list-item-icon(
                    v-if="action.icon"
                    class="ma-0 h-100 d-flex align-center"
                  )
                    e-svg-icon(:name="action.icon")
          template(v-else)
            v-list-item(
              v-if="isVisible(action, item)"
              :key="idx"
              @click="action.call(item, true)"
              class="menu-list__item"
              :data-test="`action-${action?.name}`"
            )
              v-list-item-title(
                class="text-14px"
                :class="[isRemoveAction(action) ? 'color-error' : 'dark']"
              ) {{ $t(action.text) }}
              v-list-item-icon(
                v-if="action.icon"
                class="ma-0 h-100 d-flex align-center"
              )
                e-svg-icon(:name="resolveIcon(action.icon)")
</template>

<script>
import checkVisibility from '~/mixins/checkVisibility'
import EButtonEntityView from '~/components/elements/buttons/e-button-entity-view'
import EOrganizationUserNotificationsStatusIcon from '~/modules/organizations/components/elements/e-organization-user-notifications-status-icon.vue'

export default {
  name: 'TOrmTableActions',
  components: {
    EButtonEntityView,
    EOrganizationUserNotificationsStatusIcon
  },
  mixins: [checkVisibility],
  props: {
    actions: {
      type: Array,
      default: () => [],
      required: true
    },
    item: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    },
    customIcon: {
      type: String,
      default: 'mdi-dots-horizontal'
    },
    activatorOptions: {
      type: Object,
      default: () => ({})
    },
    menuOptions: {
      type: Object,
      default: () => ({ bottom: true, left: true, nudgeBottom: '40' })
    },
    config: {
      type: Object,
      default: () => ({})
    },
    rowIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isOpened: false
    }
  },
  computed: {
    showActions() {
      return this.actions.some(action => this.isVisible(action, this.item))
    },
    prependComponent() {
      return this._.get(this.config, 'prependComponent', null)
    },
    prependComponentProps() {
      return this._.get(this.config, 'prependComponentProps', null)
    }
  },
  watch: {
    isOpened(val) {
      this.$emit('input', val)
    }
  },
  methods: {
    resolveIcon(icon) {
      return this._.isFunction(icon) ? icon(this.item) : icon
    },
    isRemoveAction(action) {
      return ['delete', 'remove'].includes(action.name)
    },
    isDisabled(action) {
      return action.disabled ? action.disabled(this.item) : false
    },
    handleOptionClick(option) {
      option.call(this.item, option.value)
    },
    setProps(componentProps) {
      return this._.isFunction(componentProps) ? componentProps(this.item) : componentProps
    }
  }
}
</script>

<style scoped lang="scss">
.t-orm-table-actions {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
